import { supabase } from '../../lib/supabase';
import type { Database } from '../../types/database.types';

type Milestone = Database['public']['Tables']['milestones']['Row'];
type MilestoneInsert = Database['public']['Tables']['milestones']['Insert'];
type MilestoneUpdate = Database['public']['Tables']['milestones']['Update'];

/**
 * API service for Milestone CRUD operations
 */
export const milestonesApi = {
  /**
   * Get all milestones for a specific project
   */
  async getByProjectId(projectId: number): Promise<{ data: Milestone[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('milestones')
        .select('*')
        .eq('project_id', projectId)
        .order('target_date', { ascending: true });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error : new Error('Unknown error'),
      };
    }
  },

  /**
   * Get a single milestone by ID.
   * Returns { data: null, error: null } when the milestone does not exist (PGRST116).
   */
  async getById(id: number): Promise<{ data: Milestone | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('milestones')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return { data: null, error: null };
        throw error;
      }
      return { data, error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error : new Error('Unknown error'),
      };
    }
  },

  /**
   * Create a new milestone
   */
  async create(
    milestone: MilestoneInsert
  ): Promise<{ data: Milestone | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('milestones')
        .insert(milestone)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error : new Error('Unknown error'),
      };
    }
  },

  /**
   * Update an existing milestone.
   * Returns an error immediately if no fields are provided.
   */
  async update(
    id: number,
    updates: MilestoneUpdate
  ): Promise<{ data: Milestone | null; error: Error | null }> {
    if (Object.keys(updates).length === 0) {
      return { data: null, error: new Error('No fields provided for update') };
    }
    try {
      const { data, error } = await supabase
        .from('milestones')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error : new Error('Unknown error'),
      };
    }
  },

  /**
   * Delete a milestone.
   * Returns deleted: true if a row was removed, false if the ID did not match any row.
   */
  async delete(id: number): Promise<{ deleted: boolean; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('milestones')
        .delete()
        .eq('id', id)
        .select('id');

      if (error) throw error;
      return { deleted: (data?.length ?? 0) > 0, error: null };
    } catch (error) {
      return {
        deleted: false,
        error: error instanceof Error ? error : new Error('Unknown error'),
      };
    }
  },

  async getByProjectIds(
    projectIds: number[]
  ): Promise<{ data: Milestone[] | null; error: Error | null }> {
    if (projectIds.length === 0) return { data: [], error: null };
    try {
      const { data, error } = await supabase
        .from('milestones')
        .select('*')
        .in('project_id', projectIds)
        .order('target_date', { ascending: true });
      if (error) throw error;
      return { data: data ?? [], error: null };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error : new Error('Unknown error'),
      };
    }
  },
};
